import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called when the modal requests to close. */
  onClose: () => void;
  /** Panel width preset. @default 'md' */
  size?: ModalSize;
  /** Close when clicking the backdrop. @default true */
  closeOnBackdrop?: boolean;
  /** Close when pressing Escape. @default true */
  closeOnEscape?: boolean;
  children: ReactNode;
}

export interface ModalHeaderProps {
  title: string;
  subtitle?: string;
  /** Hide the built-in close (X) button. @default false */
  hideClose?: boolean;
}

export interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

interface ModalCtx {
  titleId: string;
  onClose: () => void;
}

const ModalContext = createContext<ModalCtx | null>(null);

function useModalCtx() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('Modal sub-component used outside <Modal>');
  return ctx;
}

/** The id the dialog's `aria-labelledby` points at. A modal that renders its
 *  own heading instead of <Modal.Header> must put this id on that heading, or
 *  the dialog has no accessible name. */
export function useModalTitleId() {
  return useModalCtx().titleId;
}

/* ------------------------------------------------------------------ */
/*  Size map                                                           */
/* ------------------------------------------------------------------ */

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-5xl',
};

/* ------------------------------------------------------------------ */
/*  Focus trap                                                         */
/* ------------------------------------------------------------------ */

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function trapFocus(e: KeyboardEvent, container: HTMLElement) {
  if (e.key !== 'Tab') return;
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE),
  );
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

/* ------------------------------------------------------------------ */
/*  Root — <Modal>                                                     */
/* ------------------------------------------------------------------ */

function Modal({
  open,
  onClose,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  children,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // --- Escape key ---
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, closeOnEscape, onClose]);

  // --- Focus trap & restore ---
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    // Small delay to let React paint the portal before focusing
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel).focus();
    });

    const handler = (e: KeyboardEvent) => {
      if (panelRef.current) trapFocus(e, panelRef.current);
    };
    document.addEventListener('keydown', handler);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handler);
      previousFocusRef.current?.focus();
    };
  }, [open]);

  // --- Scroll lock ---
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // --- Backdrop click ---
  const handleBackdrop = useCallback(() => {
    if (closeOnBackdrop) onClose();
  }, [closeOnBackdrop, onClose]);

  if (!open) return null;

  return createPortal(
    <ModalContext.Provider value={{ titleId, onClose }}>
      <div
        className="fixed inset-0 z-skip flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-xs animate-fade-in"
          onClick={handleBackdrop}
        />

        {/* Panel */}
        <div
          ref={panelRef}
          tabIndex={-1}
          className={clsx(
            'relative mx-4 flex w-full flex-col rounded-xl border border-border bg-surface shadow-xl animate-scale-in',
            'max-h-[90vh] outline-hidden',
            SIZE_CLASS[size],
          )}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function Header({ title, subtitle, hideClose = false }: ModalHeaderProps) {
  const { titleId, onClose } = useModalCtx();

  return (
    <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
      <div className="min-w-0">
        <h2
          id={titleId}
          className="text-sm font-semibold text-ink"
        >
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-2xs text-ink-muted">{subtitle}</p>
        )}
      </div>

      {!hideClose && (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-md p-1 text-ink-muted transition-colors hover:bg-surface-1 hover:text-ink focus:outline-hidden focus:ring-2 focus:ring-primary/40"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
Header.displayName = 'Modal.Header';

function Body({ children, className }: ModalBodyProps) {
  return (
    <div className={clsx('flex-1 overflow-y-auto px-5 py-4', className)}>
      {children}
    </div>
  );
}
Body.displayName = 'Modal.Body';

function Footer({ children, className }: ModalFooterProps) {
  return (
    <div
      className={clsx(
        'flex items-center justify-end gap-2 border-t border-border px-5 py-3',
        className,
      )}
    >
      {children}
    </div>
  );
}
Footer.displayName = 'Modal.Footer';

/* ------------------------------------------------------------------ */
/*  Attach sub-components                                              */
/* ------------------------------------------------------------------ */

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;

export { Modal };
